import type { AppConfig, PhoneMessage, WebMessage } from '@tellvo/setup-protocol';

/**
 * The page as an editor of its own (protocol 3): what the phone last reported, plus the changes
 * made here that the phone has not confirmed yet. The page shows the two together, so editing
 * works the same whether the phone is connected or in the background; the changes go out when
 * it is there and stay until it answers. Pure, for tests — `Syncer` puts it on the wire.
 */

/** The changes the page keeps until the phone has them. */
export type Change = Extract<WebMessage, { kind: 'upsert' | 'delete' | 'enable' | 'accountPush' | 'reorder' | 'settings' | 'feature' }>;

export function isChange(message: WebMessage): message is Change {
  return ['upsert', 'delete', 'enable', 'accountPush', 'reorder', 'settings', 'feature'].includes(message.kind);
}

export type AccountMark =
  | { kind: 'pending' }
  | { kind: 'deleting' }
  /** `message` is the phone's own words; `rejected` means the user said no on the phone. */
  | { kind: 'failed'; message?: string; rejected?: boolean };

export interface View {
  config: AppConfig;
  marks: Map<string, AccountMark>;
  /** Changes still waiting for the phone. */
  pending: number;
}

interface Entry {
  change: Change;
  failed?: { message?: string; rejected?: boolean };
}

/** Ids compare case-insensitively: the phone writes UUIDs upper-case in snapshots, lower-case in replies. */
const norm = (id: string): string => id.toLowerCase();
const accountId = (account: Record<string, unknown>): string => norm(String(account['id'] ?? ''));

export class SyncModel {
  private phone: AppConfig | null = null;
  private entries: Entry[] = [];

  get hasSnapshot(): boolean {
    return this.phone !== null;
  }

  /**
   * A change made on the page. A newer upsert of the same account replaces an older one; so
   * does a newer order the older one, since each carries the whole list. The phone applies an
   * order only over exactly its own accounts, so the list is cut to those, any it misses at the end.
   */
  record(change: Change): void {
    change = normalized(change);
    if (change.kind === 'upsert') {
      const id = accountId(change.account);
      this.entries = this.entries.filter((e) => !(e.change.kind === 'upsert' && accountId(e.change.account) === id));
    } else if (change.kind === 'accountPush') {
      const id = change.accountId;
      this.entries = this.entries.filter((e) => !(e.change.kind === 'accountPush' && e.change.accountId === id));
    } else if (change.kind === 'reorder') {
      this.entries = this.entries.filter((e) => e.change.kind !== 'reorder');
      if (this.phone) {
        const own = this.phoneIds();
        const wanted = change.accountIds;
        change = { kind: 'reorder', accountIds: [...wanted.filter((id) => own.includes(id)), ...own.filter((id) => !wanted.includes(id))] };
      }
    }
    this.entries.push({ change });
  }

  /** The phone's accounts in its order. */
  private phoneIds(): string[] {
    return this.phone?.accounts.map((a) => accountId(a.account)) ?? [];
  }

  /** Whether the phone itself has the account (not just a change made here). */
  phoneHas(id: string): boolean {
    return this.phone?.accounts.some((a) => accountId(a.account) === norm(id)) ?? false;
  }

  /** An account that exists only as a change here is simply dropped, nothing to tell the phone. */
  forget(id: string): void {
    id = norm(id);
    this.entries = this.entries.filter((e) => !('accountId' in e.change && e.change.accountId === id)
      && !(e.change.kind === 'upsert' && accountId(e.change.account) === id));
  }

  /** Everything to (re)send: on every connection the phone gets all it has not confirmed. */
  outbox(): Change[] {
    return this.entries.filter((e) => !e.failed).map((e) => e.change);
  }

  /** The phone's answer settles what it confirms. */
  onPhone(message: PhoneMessage): void {
    switch (message.kind) {
      case 'snapshot':
        this.phone = structuredClone(message.config);
        for (const entry of this.phone.accounts) entry.account['id'] = accountId(entry.account);
        // Toggles, settings and the order are settled once the phone's state shows them; a
        // refused change of an account the phone has gives way to what the phone has. An order
        // over a different set of accounts than the phone now has is one the phone will not
        // apply: it is dropped, and the cards stand as the phone has them.
        this.entries = this.entries.filter((e) => !this.visible(e.change, message.config)
          && !(e.failed && e.change.kind === 'upsert' && this.phoneHas(accountId(e.change.account)))
          && !(e.change.kind === 'reorder' && !sameSet(e.change.accountIds, this.phoneIds())));
        return;
      case 'saved':
        this.drop('upsert', norm(message.accountId));
        return;
      case 'confirmed':
        if (!message.accountId) return;
        if (message.action === 'delete') this.drop('delete', norm(message.accountId));
        else if (message.ok) this.drop('upsert', norm(message.accountId));
        else this.fail(norm(message.accountId), { rejected: true });
        return;
      case 'error':
        if (message.accountId) this.fail(norm(message.accountId), { message: message.message });
        return;
      default:
        return;
    }
  }

  /** What the page shows: the phone's state with the unconfirmed changes on top. */
  view(): View | null {
    if (!this.phone) return null;
    const config: AppConfig = structuredClone(this.phone);
    const marks = new Map<string, AccountMark>();
    for (const { change, failed } of this.entries) {
      switch (change.kind) {
        case 'upsert': {
          const id = accountId(change.account);
          const existing = config.accounts.find((a) => accountId(a.account) === id);
          if (existing) {
            existing.account = { ...change.account };
            existing.hasPassword = existing.hasPassword || change.password !== undefined;
          } else {
            config.accounts.push({ account: { ...change.account }, hasPassword: change.password !== undefined, registration: { state: 'unregistered' } });
          }
          marks.set(id, failed ? { kind: 'failed', ...failed } : { kind: 'pending' });
          break;
        }
        case 'delete':
          marks.set(change.accountId, { kind: 'deleting' });
          break;
        case 'enable': {
          const entry = config.accounts.find((a) => accountId(a.account) === change.accountId);
          if (entry) entry.account['enabled'] = change.enabled;
          if (!marks.has(change.accountId)) marks.set(change.accountId, { kind: 'pending' });
          break;
        }
        case 'accountPush': {
          const entry = config.accounts.find((a) => accountId(a.account) === change.accountId);
          if (entry?.pushSwitch) entry.pushSwitch = { ...entry.pushSwitch, on: change.enabled };
          break;
        }
        case 'reorder': {
          // The named accounts in that order, anything else (new here, not on the phone yet) after them.
          const order = new Map(change.accountIds.map((id, i) => [id, i]));
          config.accounts.sort((a, b) => (order.get(accountId(a.account)) ?? order.size) - (order.get(accountId(b.account)) ?? order.size));
          // A card that is not where the phone has it waits, like a toggle does.
          const own = this.phoneIds();
          config.accounts.map((a) => accountId(a.account)).filter((id) => own.includes(id)).forEach((id, i) => {
            if (id !== own[i] && !marks.has(id)) marks.set(id, { kind: 'pending' });
          });
          break;
        }
        case 'settings':
          Object.assign(config.settings, change.patch);
          break;
        case 'feature':
          config.features[change.key].enabled = change.enabled;
          break;
      }
    }
    return { config, marks, pending: this.outbox().length };
  }

  private drop(kind: 'upsert' | 'delete', id: string): void {
    const index = this.entries.findIndex((e) => e.change.kind === kind && (kind === 'upsert'
      ? accountId((e.change as Extract<Change, { kind: 'upsert' }>).account) === id
      : (e.change as Extract<Change, { kind: 'delete' }>).accountId === id));
    if (index >= 0) this.entries.splice(index, 1);
  }

  /**
   * The phone refused a change. For an account it has, the change is dropped: the page shows
   * the phone's state again, and the notice says why. A new account the phone does not have
   * stays, marked, so it can be saved again or removed.
   */
  private fail(id: string, failed: { message?: string; rejected?: boolean }): void {
    if (this.phoneHas(id)) {
      this.entries = this.entries.filter((e) => !(e.change.kind === 'upsert' && accountId(e.change.account) === id));
      return;
    }
    for (const e of this.entries) {
      if (e.change.kind === 'upsert' && accountId(e.change.account) === id) e.failed = failed;
    }
  }

  private visible(change: Change, config: AppConfig): boolean {
    switch (change.kind) {
      case 'enable': {
        const entry = config.accounts.find((a) => accountId(a.account) === change.accountId);
        return !entry || (entry.account['enabled'] ?? true) === change.enabled;
      }
      case 'accountPush': {
        // Settled when the phone shows it, or when it cannot be turned on there (the phone said why).
        const entry = config.accounts.find((a) => accountId(a.account) === change.accountId);
        const sw = entry?.pushSwitch;
        return !sw || sw.on === change.enabled || (change.enabled && !sw.available);
      }
      case 'settings':
        return Object.entries(change.patch).every(([k, v]) => (config.settings as Record<string, unknown>)[k] === v);
      case 'feature':
        return config.features[change.key].enabled === change.enabled || !config.features[change.key].available;
      case 'reorder':
        return config.accounts.map((a) => accountId(a.account)).join('\n') === change.accountIds.join('\n');
      default:
        return false;
    }
  }
}

/** What `Syncer` needs from the session: send when the phone is there, say false when not. */
export interface Line {
  send(message: WebMessage): boolean;
}

/**
 * The model on the wire: a change is recorded and sent at once if the phone is there; when the
 * phone (re)appears, everything unconfirmed goes again. Messages that only make sense live —
 * the editor preview, a check, the log — go out only while the phone is there.
 */
export class Syncer {
  constructor(private readonly line: Line, readonly model = new SyncModel()) {}

  send(message: WebMessage): void {
    if (isChange(message)) this.model.record(message);
    this.line.send(message);
  }

  /** The phone is back (its snapshot arrived): replay what it has not confirmed. */
  flush(): void {
    for (const change of this.model.outbox()) {
      if (!this.line.send(change)) return;
    }
  }

  onPhone(message: PhoneMessage): void {
    this.model.onPhone(message);
  }
}

/** The same ids, whatever their order. */
function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

function normalized(change: Change): Change {
  switch (change.kind) {
    case 'upsert': return { ...change, account: { ...change.account, id: accountId(change.account) } };
    case 'delete': case 'enable': case 'accountPush': return { ...change, accountId: norm(change.accountId) };
    case 'reorder': return { ...change, accountIds: change.accountIds.map(norm) };
    default: return change;
  }
}
